import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import JobCard from "@/components/JobCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Stethoscope, Zap, Plus, Bell } from "lucide-react";
import { toast } from "sonner";

const EMP_TYPES = ["All", "Full-time", "Part-time", "Per Diem", "Contract"];

function AlertSubscribe() {
  const [email, setEmail] = useState("");
  const [cats, setCats] = useState([]);
  const [showCats, setShowCats] = useState(false);
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meta/categories").then((r) => setRoles(r.data.job_role_categories || [])).catch(() => {});
  }, []);

  const toggleCat = (c) => setCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/alerts/subscribe", { email, role_categories: cats });
      const suffix = cats.length ? ` for ${cats.length} role${cats.length > 1 ? "s" : ""}` : "";
      toast.success(`Subscribed — weekly digest${suffix} on its way`);
      setEmail("");
    } catch { toast.error("Could not subscribe. Try again."); }
    setBusy(false);
  };

  return (
    <div className="mt-6 max-w-xl">
      <form onSubmit={submit} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full p-1.5">
        <Bell className="w-4 h-4 ml-3 text-[#DDA74A]" />
        <Input
          data-testid="jobs-alert-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email — get a weekly digest"
          type="email"
          className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-[#F7F4EB] placeholder:text-white/50"
        />
        <Button data-testid="jobs-alert-submit" disabled={busy} type="submit" className="abm-btn-primary rounded-full h-9 px-4 text-sm">
          {busy ? "Subscribing…" : "Alert me"}
        </Button>
      </form>
      <button
        type="button"
        data-testid="jobs-alert-cats-toggle"
        onClick={() => setShowCats((v) => !v)}
        className="mt-2 ml-3 text-xs text-[#DDA74A] hover:underline"
      >
        {showCats ? "Hide role filters" : `Pick roles to hear about${cats.length ? ` (${cats.length} selected)` : " · optional"}`}
      </button>
      {showCats && roles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 max-w-xl">
          {roles.map((r) => (
            <button
              type="button"
              key={r}
              data-testid={`alert-cat-${r}`}
              onClick={() => toggleCat(r)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${cats.includes(r) ? "bg-[#DDA74A] text-[#1E3A2F] border-[#DDA74A]" : "border-white/20 text-[#F7F4EB] hover:bg-white/10"}`}
            >
              {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState(["All"]);
  const [loading, setLoading] = useState(true);
  const q = params.get("q") || "";
  const role = params.get("role") || "All";
  const employment = params.get("employment") || "All";
  const urgent = params.get("urgent") === "true";

  useEffect(() => {
    setLoading(true);
    const query = { limit: 60 };
    if (q) query.q = q;
    if (role !== "All") query.role_category = role;
    if (employment !== "All") query.employment_type = employment;
    if (urgent) query.start_asap = true;
    api.get("/jobs", { params: query })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
        if (r.data.role_categories) setRoles(["All", ...r.data.role_categories]);
      })
      .finally(() => setLoading(false));
  }, [q, role, employment, urgent]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "All" || v === false) p.delete(k); else p.set(k, v);
    setParams(p);
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-[#1E3A2F] text-[#F7F4EB]">
        <div className="absolute inset-0 opacity-[0.08] abm-kente" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-20 relative">
          <div className="inline-flex items-center gap-2 abm-chip !bg-[#DDA74A]/15 !text-[#DDA74A] !border-[#DDA74A]/40">
            <Stethoscope className="w-3 h-3" /> Healthcare hiring board
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Healthcare jobs — <span className="text-[#DDA74A]">start this week.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-[#A3B3AA] text-base sm:text-lg leading-relaxed">
            Care roles hiring right now across Washington. RN, LPN, CNA, HHA, caregivers, medical
            assistants — all posted by community employers who need help immediately.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/post/job"
              data-testid="jobs-post-btn"
              className="inline-flex items-center gap-2 rounded-full abm-btn-primary px-5 h-11 font-medium"
            >
              <Plus className="w-4 h-4" /> Post a job
            </Link>
            <button
              data-testid="jobs-urgent-toggle"
              onClick={() => setParam("urgent", urgent ? null : "true")}
              className={`inline-flex items-center gap-2 rounded-full px-5 h-11 font-medium border transition ${urgent ? "bg-[#DDA74A] text-[#1E3A2F] border-[#DDA74A]" : "border-white/30 text-[#F7F4EB] hover:bg-white/10"}`}
            >
              <Zap className="w-4 h-4" /> {urgent ? "Showing immediate only" : "Immediate openings"}
            </button>
          </div>
          <AlertSubscribe />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-2xl bg-white border border-[#E2DED0] p-4 mb-8 grid md:grid-cols-[1fr_auto_auto] gap-3 items-center">
          <div className="flex items-center gap-2 bg-[#F7F4EB] rounded-full px-3">
            <Search className="w-4 h-4 text-[#5C5C5C]" />
            <Input
              data-testid="jobs-search-input"
              defaultValue={q}
              onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.currentTarget.value); }}
              placeholder="Search: RN, caregiver, Seattle, night shift…"
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <Select value={role} onValueChange={(v) => setParam("role", v)}>
            <SelectTrigger data-testid="jobs-role-select" className="min-w-[220px]"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={employment} onValueChange={(v) => setParam("employment", v)}>
            <SelectTrigger data-testid="jobs-employment-select" className="min-w-[160px]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>{EMP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="text-sm text-[#5C5C5C] mb-4">{total} open role{total === 1 ? "" : "s"}</div>

        {loading ? (
          <div className="text-center py-20 text-[#5C5C5C]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
            <div className="font-display text-2xl text-[#1E3A2F]">No open jobs match your filters</div>
            <p className="text-sm text-[#5C5C5C] mt-2">Clear filters or be the first to post an opening.</p>
            <Link to="/post/job" className="inline-block mt-5 rounded-full abm-btn-primary px-5 h-10 leading-10 text-white">Post a job</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        )}
      </div>
    </div>
  );
}
